import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Add a vehicle and record its setup | Paddiq';

export function GarageVehicleSetupPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Garage guide</p>
            <h1>Add a vehicle and record its setup.</h1>
            <p className="guide-hero__lead">
              Give a mechanic one place to update the current setup, readiness, photos, and custom
              fields—with changes saved automatically.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/karts')}
              >
                Open Garage
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/add-team-members"
              >
                Add a driver first
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/add-vehicle-setup" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#open-garage">Open Garage</a>
                </li>
                <li>
                  <a href="#identity">Create the vehicle</a>
                </li>
                <li>
                  <a href="#assignment">Assign and describe it</a>
                </li>
                <li>
                  <a href="#setup">Record a setup baseline</a>
                </li>
                <li>
                  <a href="#verify">Check saved changes</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>
                  Add the driver to Team first if you want to assign the vehicle while creating it.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="open-garage">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Garage access</p>
                  <h2>Open Garage and check that you can add vehicles.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Sign in and open <strong>Garage</strong> from the main navigation.
                    </li>
                    <li>
                      Select <strong>Add your first vehicle</strong> in an empty Garage, or{' '}
                      <strong>Add Vehicle</strong> above an existing fleet.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    If the add action is not shown, ask a team owner, coach, or mechanic to create
                    the vehicle. In a family workspace, a parent manager can manage vehicles.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="identity">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Identity</p>
                  <h2>Create a record your team can recognize.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Choose <strong>Kart</strong> or <strong>Other motorsport</strong> for the
                      vehicle type.
                    </li>
                    <li>Add a clear nickname when your team uses one.</li>
                    <li>Set the fleet status to match how the vehicle should appear in Garage.</li>
                  </ol>
                  <p>
                    Paddiq generates the displayed vehicle ID when you add a vehicle from the web.
                    Nickname, class, and status tag give the record the trackside labels your team
                    recognizes.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="assignment">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Driver and hardware</p>
                  <h2>Assign the driver and add useful equipment details.</h2>
                  <p>
                    Choose the intended person in <strong>Assigned driver</strong>, or leave the
                    vehicle Unassigned until the roster is ready. You can also add a class and
                    status tag for filtering.
                  </p>
                  <p>
                    Expand <strong>Hardware</strong> to record details such as chassis or platform,
                    engine, serial numbers, engine hours, and odometer. These fields are optional;
                    use the ones your team can keep current.
                  </p>
                  <div className="guide-callout">
                    A missing driver must be added to the same workspace first. Follow the{' '}
                    <a className="guide-text-link" href="/resources/add-team-members">
                      drivers and team members guide
                    </a>{' '}
                    before returning to Garage.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="setup">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Setup</p>
                  <h2>Record the current baseline configuration.</h2>
                  <p>
                    When setup management is available in the workspace, expand{' '}
                    <strong>Setup</strong> and enter the values you want to compare later:
                  </p>
                  <ul>
                    <li>front and rear track width, or stance for another vehicle type;</li>
                    <li>seat or driver position;</li>
                    <li>driver and axle gear counts;</li>
                    <li>front and rear tire pressure; and</li>
                    <li>total weight.</li>
                  </ul>
                  <p>
                    Treat these as the vehicle's current baseline. Change them when the physical
                    setup changes so the latest values remain meaningful for session review.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="verify">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Save and verify</p>
                  <h2>Open the vehicle drawer and make trackside changes.</h2>
                  <ol className="guide-instructions">
                    <li>Select a vehicle row to open its mechanic-focused drawer.</li>
                    <li>Keep the vehicle photo visible and replace it when the kart changes.</li>
                    <li>
                      Update Quick Setup, assignment, readiness, hours, status, or custom fields.
                    </li>
                  </ol>
                  <p>
                    Changes save when you leave a field. One editing visit updates one working
                    snapshot, so changing several values does not create a burst of setup history.
                    Use Details and History in the same drawer for less common work.
                  </p>
                  <div className="guide-ready">
                    <h3>The Garage record is ready when:</h3>
                    <ul>
                      <li>the intended vehicle appears in Garage;</li>
                      <li>its driver and fleet status are correct;</li>
                      <li>the hardware details you rely on are present;</li>
                      <li>the drawer reports that your latest changes are saved; and</li>
                      <li>custom fields and the current setup show the values you expect.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If the Garage record is not ready</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Add Vehicle is unavailable</h3>
                    <p>
                      Your role cannot manage the fleet in this workspace. Ask an owner, coach,
                      mechanic, or family-workspace parent manager to add it.
                    </p>
                  </article>
                  <article>
                    <h3>The driver is missing</h3>
                    <p>
                      Confirm the driver exists in Team for the same workspace, then reopen the
                      vehicle form.
                    </p>
                  </article>
                  <article>
                    <h3>The setup controls are absent</h3>
                    <p>
                      Setup management is not available for every workspace. Save the vehicle
                      identity and assignment, then ask the workspace owner to check its access.
                    </p>
                  </article>
                  <article>
                    <h3>The wrong vehicle was assigned</h3>
                    <p>
                      Open the vehicle drawer and change <strong>Assigned driver</strong> under{' '}
                      <strong>Assignment &amp; readiness</strong>. Wait for the change to save.
                      Correct an existing session separately under <strong>Assignments</strong> in{' '}
                      <strong>Session &amp; laps</strong>.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready to organize the fleet?</p>
                  <h2>Add the vehicle before the next upload.</h2>
                  <p>The Garage record can then be selected as context for a session.</p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/karts')}
                  >
                    Open Garage
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
